import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { ShoppingCart, X, Plus, Minus, Trash2, CheckCircle, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export const CartDrawer = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartCount, clearCart, isCartOpen, setIsCartOpen } = useCart();
  
  const handleClearCart = () => {
    if (window.confirm('Weet je zeker dat je je winkelmandje wilt leegmaken?')) {
      clearCart();
      toast.success('Winkelmandje is leeggemaakt');
    }
  };
  
  const handleContinueShopping = () => {
    setIsCartOpen(false);
    setTimeout(() => navigate('/shop'), 100);
  };
  
  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };
  
  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <ShoppingCart className="w-5 h-5 text-earth-brown" />
          {getCartCount() > 0 && (
            <span className="absolute -top-1 -right-1 bg-terra-beige text-white text-xs rounded-none w-5 h-5 flex items-center justify-center font-normal">
              {getCartCount()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-sweet-sans text-2xl text-earth-brown uppercase tracking-wide">Winkelmandje</SheetTitle>
        </SheetHeader>
        
        {/* Success Message Banner */}
        {cart.length > 0 && (
          <div className="mt-6 bg-green-50 border border-green-200  p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-sweet-sans text-green-800 text-sm mb-2">
                  Product toegevoegd aan winkelmandje!
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleContinueShopping}
                    className="border-green-600 text-green-700 hover:bg-green-50 font-sweet-sans text-xs"
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" />
                    Verder winkelen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClearCart}
                    className="border-red-500 text-red-600 hover:bg-red-50 font-sweet-sans text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Leegmaken
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCheckout}
                    className="bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet-sans text-xs"
                  >
                    Afrekenen →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-6 flex flex-col h-[calc(100vh-220px)]">
          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <ShoppingCart className="w-16 h-16 text-terra-beige/30 mb-4" />
              <p className="font-sweet-sans text-almost-black/60">Je winkelmandje is leeg</p>
              <Button 
                onClick={handleContinueShopping}
                className="mt-4 bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet-sans"
              >
                Start met winkelen
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-4 p-4 bg-white ">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-20 h-20 object-cover "
                    />
                    <div className="flex-1">
                      <h3 className="font-sweet-sans text-almost-black text-sm line-clamp-2">
                        {item.name}
                      </h3>
                      <p className="font-sweet-sans text-xs text-almost-black/60 mb-2">{item.size}</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-6 h-6 rounded-none border border-terra-beige/30 flex items-center justify-center hover:bg-terra-beige/10"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-sweet-sans text-sm w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 rounded-none border border-terra-beige/30 flex items-center justify-center hover:bg-terra-beige/10"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-almost-black/40 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <p className="font-sweet-sans text-lg font-semibold text-earth-brown">
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-terra-beige/20 pt-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-sweet-sans text-lg text-earth-brown uppercase tracking-wide">Totaal</span>
                  <span className="font-sweet-sans text-2xl font-bold text-earth-brown">
                    €{getCartTotal().toFixed(2)}
                  </span>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet uppercase tracking-wider"
                >
                  Afrekenen
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};